import { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle2, HardHat, Languages, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../services/api";
import { formatPhoneNumber, isCompletePhone } from "../lib/formatters";
import { APPLY_T, QUESTION_KEYS } from "../lib/applyTranslations";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

let _turnstileScriptPromise = null;
const loadTurnstileScript = () => {
  if (_turnstileScriptPromise) return _turnstileScriptPromise;
  _turnstileScriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return _turnstileScriptPromise;
};

const YesNo = ({ value, onChange, testId, t }) => (
  <div className="flex gap-2">
    {[true, false].map((v) => (
      <button
        key={String(v)}
        type="button"
        onClick={() => onChange(v)}
        data-testid={`${testId}-${v ? "yes" : "no"}`}
        className={`px-5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
          value === v
            ? v
              ? "bg-emerald-600 text-white border-emerald-600"
              : "bg-red-600 text-white border-red-600"
            : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
        }`}
      >
        {v ? t.yes : t.no}
      </button>
    ))}
  </div>
);

const LangToggle = ({ lang, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(lang === "en" ? "es" : "en")}
    className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:border-slate-500 transition-colors"
    data-testid="apply-lang-toggle"
  >
    <Languages className="w-3.5 h-3.5" /> {APPLY_T[lang].langLabel}
  </button>
);

const Header = ({ t, lang, setLang }) => (
  <div className="relative text-center mb-6">
    <div className="absolute right-0 top-0">
      <LangToggle lang={lang} onChange={setLang} />
    </div>
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 text-white mb-3">
      <HardHat className="w-7 h-7" />
    </div>
    <h1 className="text-3xl font-bold text-slate-900">{t.title}</h1>
    <p className="mt-2 text-slate-600">{t.subtitle}</p>
  </div>
);

const ApplyPage = () => {
  const [lang, setLang] = useState("en");
  const [step, setStep] = useState("position");
  const [position, setPosition] = useState(null);
  const [years, setYears] = useState("");
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "", company: "",
  });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);
  const t = APPLY_T[lang];

  useEffect(() => {
    if (step !== "form") return undefined;
    let mounted = true;
    (async () => {
      try {
        await loadTurnstileScript();
        if (!mounted || !widgetRef.current || !window.turnstile || widgetIdRef.current) return;
        widgetIdRef.current = window.turnstile.render(widgetRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "auto",
          language: lang,
          callback: (token) => setTurnstileToken(token),
          "expired-callback": () => setTurnstileToken(""),
          "error-callback": () => setTurnstileToken(""),
        });
      } catch (err) {
        // Widget failed to load; submit will show a friendly error.
      }
    })();
    return () => {
      mounted = false;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (e) {}
        widgetIdRef.current = null;
      }
    };
  }, [step, lang]);

  const resetTurnstile = () => {
    setTurnstileToken("");
    if (widgetIdRef.current && window.turnstile) {
      try { window.turnstile.reset(widgetIdRef.current); } catch (e) {}
    }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const choosePosition = (p) => {
    setPosition(p);
    if (p === "mason") setStep("experience");
    else { setYears(""); setStep("form"); }
  };

  const continueFromExperience = () => {
    if (years === "" || Number.isNaN(Number(years)) || Number(years) < 0) {
      toast.error(t.errYears);
      return;
    }
    setStep("form");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error(t.errFill);
      return;
    }
    if (!isCompletePhone(form.phone)) {
      toast.error(t.errPhone);
      return;
    }
    for (const key of QUESTION_KEYS) {
      if (typeof answers[key] !== "boolean") {
        toast.error(t.errAnswers);
        return;
      }
    }
    if (!turnstileToken) {
      toast.error(t.errVerify);
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(`/applications`, {
        ...form,
        ...answers,
        position,
        years_experience: position === "mason" ? Number(years) : null,
        language: lang,
        turnstile_token: turnstileToken,
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || t.errGeneric;
      toast.error(msg);
      resetTurnstile();
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="apply-success">
          <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">{t.successTitle}</h1>
          <p className="mt-3 text-slate-600 text-left" data-testid="apply-success-body">{t.successBody}</p>
          <p className="mt-3 text-slate-600 text-left">
            {t.successPhone} <a href="tel:+12542748050" className="font-semibold text-slate-900 whitespace-nowrap" data-testid="apply-success-phone">(254) 274-8050</a>.
          </p>
          {form.email && (
            <p className="mt-3 text-sm text-slate-500 text-left" data-testid="apply-success-email-note">
              {t.successEmailNote} <span className="font-medium text-slate-700">{form.email}</span>.
            </p>
          )}
          <p className="mt-4 font-semibold text-slate-900">{t.successSignature}</p>
        </div>
      </div>
    );
  }

  if (step === "position") {
    return (
      <div className="min-h-screen bg-slate-50 py-10 px-4">
        <div className="max-w-lg mx-auto">
          <Header t={t} lang={lang} setLang={setLang} />
          <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="apply-step-position">
            <h2 className="text-lg font-semibold text-slate-900 text-center">{t.positionQuestion}</h2>
            <p className="text-sm text-slate-500 text-center mt-1">{t.positionHint}</p>
            <div className="grid grid-cols-2 gap-4 mt-6">
              <button type="button" onClick={() => choosePosition("mason")} data-testid="apply-position-mason"
                className="rounded-xl border-2 border-slate-200 bg-white py-8 text-lg font-semibold text-slate-800 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition-colors">
                {t.mason}
              </button>
              <button type="button" onClick={() => choosePosition("laborer")} data-testid="apply-position-laborer"
                className="rounded-xl border-2 border-slate-200 bg-white py-8 text-lg font-semibold text-slate-800 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition-colors">
                {t.laborer}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === "experience") {
    return (
      <div className="min-h-screen bg-slate-50 py-10 px-4">
        <div className="max-w-lg mx-auto">
          <Header t={t} lang={lang} setLang={setLang} />
          <div className="bg-white rounded-2xl shadow-lg p-6 space-y-5" data-testid="apply-step-experience">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-slate-900">{t.experienceQuestion}</h2>
              <p className="text-sm text-slate-500 mt-1">{t.experienceHint}</p>
            </div>
            <div>
              <Label htmlFor="yrs">{t.yearsLabel}</Label>
              <Input id="yrs" type="number" min="0" step="1" inputMode="numeric" value={years}
                onChange={(e) => setYears(e.target.value)} placeholder={t.yearsPlaceholder}
                className="mt-1 text-lg h-12" data-testid="apply-years-experience" autoFocus />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setStep("position")} className="h-11" data-testid="apply-experience-back">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> {t.back}
              </Button>
              <Button type="button" onClick={continueFromExperience} className="flex-1 h-11 text-base" data-testid="apply-experience-continue">
                {t.continue}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <Header t={t} lang={lang} setLang={setLang} />

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 space-y-5" data-testid="apply-form">
          <div className="flex items-center justify-between rounded-lg bg-slate-100 px-3 py-2 text-sm" data-testid="apply-position-summary">
            <span className="text-slate-700">
              {t.applyingFor}: <strong>{position === "mason" ? t.mason : t.laborer}</strong>
              {position === "mason" && years !== "" && <> · {years} {t.yrsExp}</>}
            </span>
            <button type="button" onClick={() => setStep("position")} className="text-xs font-medium text-slate-600 underline hover:text-slate-900" data-testid="apply-change-position">
              {t.change}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="fn">{t.firstName}</Label>
              <Input id="fn" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="apply-first-name" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="ln">{t.lastName}</Label>
              <Input id="ln" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="apply-last-name" className="mt-1" />
            </div>
          </div>
          <div>
            <Label htmlFor="ph">{t.phone}</Label>
            <Input id="ph" type="tel" value={form.phone} onChange={(e) => set("phone", formatPhoneNumber(e.target.value))} placeholder="(555) 123-4567" data-testid="apply-phone" className={`mt-1 ${form.phone && !isCompletePhone(form.phone) ? "border-red-500 focus-visible:ring-red-500" : ""}`} />
            {form.phone && !isCompletePhone(form.phone) && (
              <p className="text-xs text-red-600 mt-1" data-testid="apply-phone-error">{t.phoneError}</p>
            )}
          </div>
          <div>
            <Label htmlFor="em">{t.email}</Label>
            <Input id="em" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" data-testid="apply-email" className="mt-1" />
          </div>

          {/* Honeypot (hidden from real users) */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.company}
            onChange={(e) => set("company", e.target.value)}
            className="hidden"
            aria-hidden="true"
          />

          <div className="pt-2 space-y-4">
            {QUESTION_KEYS.map((key) => (
              <div key={key} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-t pt-4">
                <span className="text-sm text-slate-700 pr-2">{t.questions[key]}</span>
                <YesNo
                  value={answers[key]}
                  onChange={(v) => setAnswers((p) => ({ ...p, [key]: v }))}
                  testId={`apply-q-${key}`}
                  t={t}
                />
              </div>
            ))}
          </div>

          <div className="pt-2 border-t">
            <Label className="text-sm text-slate-700">{t.verification}</Label>
            <div ref={widgetRef} data-testid="apply-turnstile" className="mt-2 flex justify-center" />
          </div>

          <Button type="submit" disabled={submitting} className="w-full h-11 text-base" data-testid="apply-submit">
            {submitting ? t.submitting : t.submit}
          </Button>
          <p className="text-xs text-center text-slate-400 leading-relaxed pt-1" data-testid="apply-eeo-statement">
            {t.eeo}
          </p>
        </form>
      </div>
    </div>
  );
};

export default ApplyPage;
